import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Divider,
  Fade,
} from '@material-ui/core';
import { Add as AddIcon, Edit as EditIcon } from '@material-ui/icons';
import { useSnackbar } from 'react-simple-snackbar';
import { fetchPrinterLocation, savePrinterLocation } from '../../api';

const PrinterLocation = () => {
  const [printerLocation, setPrinterLocation] = useState({
    locationName: '',
    printerName: '',
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openSnackbar] = useSnackbar();

  const user = JSON.parse(localStorage.getItem('profile'));

  const fetchPrinterLocationData = useCallback(async () => {
    try {
      const response = await fetchPrinterLocation(user?.userProfile?.id);
      if (response.data?.success && response.data?.data?.printerLocation) {
        setPrinterLocation(response.data.data.printerLocation);
        setIsEditMode(true);
      }
    } catch (error) {
      console.error('Error fetching printer location:', error);
    }
  }, [user?.userProfile?.id]);

  // Fetch existing printer location data
  useEffect(() => {
    fetchPrinterLocationData();
  }, [fetchPrinterLocationData]);

  const handleInputChange = (e) => {
    setPrinterLocation({
      ...printerLocation,
      [e.target.name]: e.target.value,
    });
  };

  const handleSave = async () => {
    if (!printerLocation.locationName.trim() || !printerLocation.printerName.trim()) {
      openSnackbar('Please fill in both location name and printer name');
      return;
    }

    setLoading(true);
    try {
      const response = await savePrinterLocation(user?.userProfile?.id, {
        locationName: printerLocation.locationName,
        printerName: printerLocation.printerName,
      });

      if (response.data?.success) {
        openSnackbar('Printer location saved successfully!');
        setIsEditMode(true);
        setOpenDialog(false);
        // Update localStorage
        const currentProfile = JSON.parse(localStorage.getItem('profile')) || {};
        const updatedProfile = {
          ...currentProfile,
          userProfile: {
            ...currentProfile.userProfile,
            printerLocation: printerLocation,
          },
        };
        localStorage.setItem('profile', JSON.stringify(updatedProfile));
      }
    } catch (error) {
      console.error('Error saving printer location:', error);
      openSnackbar('Failed to save printer location');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    // Reset form if canceling
    if (!isEditMode) {
      setPrinterLocation({ locationName: '', printerName: '' });
    }
  };

  return (
    <Card style={{ marginBottom: '20px' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h2">
            Printer Location Settings
          </Typography>
          {isEditMode ? (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              color="primary"
            >
              Edit
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
              color="primary"
            >
              Add Location
            </Button>
          )}
        </Box>

        {isEditMode && printerLocation.locationName && (
          <Box>
            <Typography variant="body1" gutterBottom>
              <strong>Location:</strong> {printerLocation.locationName}
            </Typography>
            <Typography variant="body1">
              <strong>Printer:</strong> {printerLocation.printerName}
            </Typography>
          </Box>
        )}

        {!isEditMode && (
          <Typography variant="body2" color="textSecondary">
            No printer location configured. Click "Add Location" to set up your printer location.
          </Typography>
        )}
      </CardContent>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditMode ? 'Edit Printer Location' : 'Add Printer Location'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: '10px' }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location Name"
                name="locationName"
                value={printerLocation.locationName}
                onChange={handleInputChange}
                variant="outlined"
                placeholder="e.g., Main Counter, Kitchen, Office"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Printer Name"
                name="printerName"
                value={printerLocation.printerName}
                onChange={handleInputChange}
                variant="outlined"
                placeholder="e.g., HP LaserJet Pro, Canon PIXMA"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default PrinterLocation;
